// src/context/AuthContext.jsx
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../utils/init-firebase";

const AuthContext = createContext();

// Session timeout duration in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;
// Warning notification time in milliseconds (5 minutes before timeout)
const WARNING_TIME = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Function to update last activity timestamp
  const updateLastActivity = useCallback(() => {
    setLastActivity(Date.now());
    setShowTimeoutWarning(false);
    setTimeRemaining(null);
  }, []);

  // Function to check session timeout
  const checkSessionTimeout = useCallback(() => {
    if (!currentUser) return;

    const timeSinceLastActivity = Date.now() - lastActivity;
    const timeUntilTimeout = SESSION_TIMEOUT - timeSinceLastActivity;

    // Show warning if within warning time
    if (timeUntilTimeout <= WARNING_TIME && timeUntilTimeout > 0) {
      setShowTimeoutWarning(true);
      setTimeRemaining(Math.ceil(timeUntilTimeout / 60000)); // Convert to minutes
    }
    // Logout if session timed out
    else if (timeUntilTimeout <= 0) {
      console.log("Session timed out");
      logout();
    }
  }, [currentUser, lastActivity]);

  // Set up activity listeners
  useEffect(() => {
    if (currentUser) {
      // Add event listeners for user activity
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      
      const handleUserActivity = () => {
        updateLastActivity();
      };

      // Add event listeners
      events.forEach(event => {
        window.addEventListener(event, handleUserActivity);
      });

      // Set up interval to check session timeout
      const intervalId = setInterval(checkSessionTimeout, 1000);

      // Cleanup function
      return () => {
        events.forEach(event => {
          window.removeEventListener(event, handleUserActivity);
        });
        clearInterval(intervalId);
      };
    }
  }, [currentUser, updateLastActivity, checkSessionTimeout]);

  // Subscribe to auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        setLastActivity(Date.now());
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Auth Functions
  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const register = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      console.error("Registration failed:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setLastActivity(0);
      setShowTimeoutWarning(false);
      setTimeRemaining(null);
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  };

  const forgotPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Password reset failed:", error);
      throw error;
    }
  };

  // Session timeout warning component
  const SessionTimeoutWarning = () => {
    if (!showTimeoutWarning) return null;

    return (
      <div className="session-timeout-warning">
        <div className="session-timeout-content">
          <h3>Session Timeout Warning</h3>
          <p>Your session will expire in {timeRemaining} minute{timeRemaining !== 1 ? 's' : ''}.</p>
          <p>Would you like to extend your session?</p>
          <div className="session-timeout-actions">
            <button onClick={updateLastActivity} className="session-timeout-button extend">
              Extend Session
            </button>
            <button onClick={logout} className="session-timeout-button logout">
              Logout Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Provide everything through context
  return (
    <AuthContext.Provider
      value={{
        currentUser,
        login,
        register,
        logout,
        forgotPassword,
        lastActivity,
        updateLastActivity,
      }}
    >
      {!loading && (
        <>
          {children}
          <SessionTimeoutWarning />
        </>
      )}
    </AuthContext.Provider>
  );
};

// Hook to use Auth context
export const useAuth = () => useContext(AuthContext);
